// History tab: reporting-year statuses Administr can actually know, snapshot
// versions, generated PDFs, unlock-for-corrections, user-entered "filed
// externally" recordkeeping, configuration copy, and the audit trail.
// Administr never submits to the IRS, so no submission/acceptance status exists.
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Camera, ChevronDown, ChevronRight, Clock, Copy, FileText, Loader2, Unlock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatDateDisplay } from "@/lib/utils";
import { Help, TermLabel, Explained } from "@/pages/employer/aca/shared/Help";

interface ACAHistoryProps {
  clientId?: number;
}

interface ReportingYear {
  id: number;
  taxYear: number;
  status: "not_started" | "in_progress" | "needs_attention" | "ready" | "finalized";
  isLocked: boolean | null;
  finalizedAt: string | null;
  finalizedByUserId: string | null;
  finalizedByName?: string | null;
  finalSnapshotId: number | null;
  filedExternallyAt: string | null;
  filedExternallyNotes: string | null;
}

interface SnapshotRow {
  id: number;
  version: number;
  status: string;
  computedAt: string | null;
  computedByUserId: string | null;
  computedByName?: string | null;
  engineVersion: string;
}

interface GeneratedPdf {
  id: number;
  employeeId: number | null;
  formType: string;
  version: number;
  pdfUrl: string | null;
  generatedAt: string;
  isCorrected?: boolean | null;
}

interface AuditRow {
  id: number;
  table: string;
  recordId: string;
  operation: string;
  changedAt: string;
  changedByUserId: string | null;
  changedByName: string | null;
  changedByRole: string | null;
  summary: string;
}

const ROLE_LABEL: Record<string, string> = {
  broker: "Broker",
  agency_member: "Agency Member",
  employer: "Employer",
  employee: "Employee",
  superadmin: "Superadmin",
  ga: "General Agent",
};

const STATUS_LABEL: Record<ReportingYear["status"], string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  needs_attention: "Needs Attention",
  ready: "Ready to Generate",
  finalized: "Finalized",
};
const STATUS_CLASS: Record<ReportingYear["status"], string> = {
  not_started: "bg-gray-400",
  in_progress: "bg-blue-500",
  needs_attention: "bg-amber-500",
  ready: "bg-emerald-500",
  finalized: "bg-green-700",
};

const fmt = (d: string | null | undefined) => (d ? new Date(d).toLocaleString() : "—");
const fmtDate = (d: string | null | undefined) => (d ? new Date(String(d).length === 10 ? `${d}T00:00:00` : d).toLocaleDateString() : "—");

export default function ACAHistory({ clientId: propClientId }: ACAHistoryProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId ?? 1;
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [expandedYear, setExpandedYear] = useState<number | null>(currentYear - 1);
  const [showCopy, setShowCopy] = useState(false);
  const [copyFromYear, setCopyFromYear] = useState<number | null>(null);
  const [copyToYear, setCopyToYear] = useState(currentYear);
  const [filedDialogYear, setFiledDialogYear] = useState<number | null>(null);
  const [filedAt, setFiledAt] = useState("");
  const [filedNotes, setFiledNotes] = useState("");

  const invalidate = () => {
    for (const key of ["/api/aca/reporting-years", "/api/aca/year/snapshots", "/api/aca/forms", "/api/aca/audit-log", "/api/aca/validation", "/api/aca/dashboard"]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const { data: years = [], isLoading } = useQuery<ReportingYear[]>({
    queryKey: ["/api/aca/reporting-years", clientId],
    queryFn: async () => { const r = await fetch(`/api/aca/reporting-years?clientId=${clientId}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load reporting years"); return r.json(); },
    enabled: !!clientId,
  });
  const { data: auditLog = [] } = useQuery<AuditRow[]>({
    queryKey: ["/api/aca/audit-log", clientId],
    queryFn: async () => { const r = await fetch(`/api/aca/audit-log?clientId=${clientId}&limit=100`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load activity"); return r.json(); },
    enabled: !!clientId,
  });

  const unlockMutation = useMutation({
    mutationFn: async (taxYear: number) => apiRequest("PUT", "/api/aca/reporting-year", { clientId, taxYear, isLocked: false }),
    onSuccess: (_r, taxYear) => { toast({ title: `Tax year ${taxYear} unlocked`, description: "Corrections will produce a new snapshot and form version; the finalized version stays on record." }); invalidate(); },
    onError: (e: any) => toast({ title: "Unlock failed", description: e.message, variant: "destructive" }),
  });
  const filedMutation = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/aca/reporting-year", { clientId, taxYear: filedDialogYear, filedExternallyAt: filedAt || null, filedExternallyNotes: filedNotes || null }),
    onSuccess: () => { toast({ title: "Recorded", description: "Filed-externally note saved (user-entered, for your records only)." }); setFiledDialogYear(null); invalidate(); },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
  const copyMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/aca/reporting-year/copy", { clientId, fromYear: copyFromYear, toYear: copyToYear }),
    onSuccess: () => { toast({ title: "Configuration copied", description: `Measurement policy and safe-harbor policies copied from ${copyFromYear} to ${copyToYear}. No employees, hours, rates, contributions or codes were copied.` }); setShowCopy(false); invalidate(); },
    onError: (e: any) => toast({ title: "Copy failed", description: e.message, variant: "destructive" }),
  });

  const yearOptions = useMemo(() => Array.from({ length: currentYear - 2014 }, (_, i) => currentYear - i), [currentYear]);
  const sortedYears = useMemo(() => [...years].sort((a, b) => b.taxYear - a.taxYear), [years]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">Reporting History</h2>
          <p className="text-sm text-muted-foreground">Calculation versions, generated forms and configuration per tax year. Administr does not submit forms to the IRS.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setCopyFromYear(sortedYears[0]?.taxYear ?? currentYear - 1); setShowCopy(true); }}>
            <Copy className="h-4 w-4 mr-2" /> Copy Year Settings
          </Button>
          <Help term="copyYearSettings" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Calendar className="h-4 w-4" /> <TermLabel term="taxYear">Reporting Years</TermLabel></CardTitle>
          <CardDescription>Status reflects what the engine can verify: Not Started, In Progress, Needs Attention (blocking exceptions), Ready to Generate, Finalized.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && sortedYears.length === 0 && <div className="text-sm text-muted-foreground">No reporting years yet. Open any ACA tab for a tax year to start one.</div>}
          {sortedYears.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead><TermLabel term="taxYear">Tax Year</TermLabel></TableHead>
                  <TableHead><TermLabel term="readiness">Status</TermLabel></TableHead>
                  <TableHead><TermLabel term="finalize">Finalized</TermLabel></TableHead>
                  <TableHead><TermLabel term="filedExternally">Filed externally (user-entered)</TermLabel></TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedYears.map((y) => (
                  <YearRows
                    key={y.taxYear}
                    year={y}
                    clientId={clientId}
                    expanded={expandedYear === y.taxYear}
                    onToggle={() => setExpandedYear(expandedYear === y.taxYear ? null : y.taxYear)}
                    onUnlock={() => unlockMutation.mutate(y.taxYear)}
                    unlocking={unlockMutation.isPending}
                    onMarkFiled={() => { setFiledDialogYear(y.taxYear); setFiledAt(y.filedExternallyAt ?? ""); setFiledNotes(y.filedExternallyNotes ?? ""); }}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <WorkforceSnapshotsCard clientId={clientId} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4" /> <TermLabel term="recentActivity">Recent Activity</TermLabel></CardTitle>
          <CardDescription>Audit trail: policy changes, overrides, recalculations, form generation, downloads and corrections.</CardDescription>
        </CardHeader>
        <CardContent>
          {auditLog.length === 0 ? (
            <div className="text-sm text-muted-foreground">No ACA activity recorded yet.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>When</TableHead><TableHead>What</TableHead><TableHead>Who</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {auditLog.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="whitespace-nowrap text-xs">{fmt(row.changedAt)}</TableCell>
                      <TableCell className="text-sm">{row.summary}</TableCell>
                      <TableCell className="text-xs text-muted-foreground" title={row.changedByUserId ? `User ID: ${row.changedByUserId}` : undefined}>
                        {row.changedByUserId ? (row.changedByName ?? "Unknown user") : "System"}{row.changedByRole ? ` (${ROLE_LABEL[row.changedByRole] ?? row.changedByRole})` : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCopy} onOpenChange={setShowCopy}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><TermLabel term="copyYearSettings">Copy Year Settings</TermLabel></DialogTitle>
            <DialogDescription>
              Copies configuration only: the measurement policy (dates shifted by the year difference) and safe-harbor policies.
              Employees, hours, rates, employee required contributions, and Line 14/15/16 codes are never copied; they are recalculated for the new year.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>From</Label>
              <Select value={copyFromYear ? String(copyFromYear) : ""} onValueChange={(v) => setCopyFromYear(Number(v))}>
                <SelectTrigger><SelectValue placeholder="Source year" /></SelectTrigger>
                <SelectContent>{sortedYears.map((y) => <SelectItem key={y.taxYear} value={String(y.taxYear)}>{y.taxYear}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Select value={String(copyToYear)} onValueChange={(v) => setCopyToYear(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopy(false)}>Cancel</Button>
            <Button onClick={() => copyMutation.mutate()} disabled={!copyFromYear || copyFromYear === copyToYear || copyMutation.isPending}>
              {copyMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />} Copy configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={filedDialogYear != null} onOpenChange={(o) => !o && setFiledDialogYear(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><TermLabel term="filedExternally">Mark {filedDialogYear} as filed externally</TermLabel></DialogTitle>
            <DialogDescription>For your own recordkeeping. Administr does not file with the IRS and cannot verify submission or acceptance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Date filed</Label>
              <Input type="date" value={filedAt} onChange={(e) => setFiledAt(e.target.value)} />
            </div>
            <div>
              <Label>Notes (transmitter, confirmation number, etc.)</Label>
              <Textarea value={filedNotes} onChange={(e) => setFiledNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFiledDialogYear(null)}>Cancel</Button>
            <Button onClick={() => filedMutation.mutate()} disabled={filedMutation.isPending}>
              {filedMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function YearRows({ year, clientId, expanded, onToggle, onUnlock, unlocking, onMarkFiled }: {
  year: ReportingYear; clientId: number; expanded: boolean; onToggle: () => void; onUnlock: () => void; unlocking: boolean; onMarkFiled: () => void;
}) {
  const { data: snapshots = [] } = useQuery<SnapshotRow[]>({
    queryKey: ["/api/aca/year/snapshots", clientId, year.taxYear],
    queryFn: async () => { const r = await fetch(`/api/aca/year/snapshots?clientId=${clientId}&taxYear=${year.taxYear}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load versions"); return r.json(); },
    enabled: expanded,
  });
  const { data: forms } = useQuery<{ version: number; records: any[]; generatedPdfs: GeneratedPdf[] }>({
    queryKey: ["/api/aca/forms", clientId, year.taxYear],
    queryFn: async () => { const r = await fetch(`/api/aca/forms?clientId=${clientId}&taxYear=${year.taxYear}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load forms"); return r.json(); },
    enabled: expanded,
  });

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
        <TableCell className="font-medium">{year.taxYear}</TableCell>
        <TableCell>
          <Explained term={year.status === "finalized" ? "finalize" : "readiness"}><Badge className={STATUS_CLASS[year.status] ?? "bg-gray-400"}>{STATUS_LABEL[year.status] ?? year.status}</Badge></Explained>
          {year.isLocked && <Explained term="finalize"><Badge variant="outline" className="ml-2">Locked</Badge></Explained>}
        </TableCell>
        <TableCell className="text-sm">{year.finalizedAt ? `${fmt(year.finalizedAt)}${year.finalizedByName ? ` by ${year.finalizedByName}` : ""}` : "—"}</TableCell>
        <TableCell className="text-sm">
          {year.filedExternallyAt ? <span>{fmtDate(year.filedExternallyAt)}{year.filedExternallyNotes ? <span className="text-muted-foreground"> · {year.filedExternallyNotes}</span> : null}</span> : <span className="text-muted-foreground">—</span>}
        </TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end gap-2">
            {year.isLocked && (
              <Explained term="snapshotVersion"><Button size="sm" variant="outline" onClick={onUnlock} disabled={unlocking}><Unlock className="h-3 w-3 mr-1" /> Unlock for corrections</Button></Explained>
            )}
            <Explained term="filedExternally"><Button size="sm" variant="outline" onClick={onMarkFiled}><FileText className="h-3 w-3 mr-1" /> Mark filed externally</Button></Explained>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1"><TermLabel term="snapshotVersion">Calculation versions</TermLabel></div>
                {snapshots.length === 0 ? <div className="text-sm text-muted-foreground">Not calculated yet.</div> : (
                  <ul className="space-y-1 text-sm">
                    {snapshots.map((s) => (
                      <li key={s.id} className="flex items-center gap-2">
                        <Badge variant={s.status === "final" ? "default" : "outline"}>v{s.version} {s.status}</Badge>
                        <span className="text-muted-foreground text-xs">{fmt(s.computedAt)}{s.computedByName ? ` · ${s.computedByName}` : ""} · engine {s.engineVersion}</span>
                        {year.finalSnapshotId === s.id && <Badge variant="secondary">finalized version</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1"><TermLabel term="form1095c">Generated forms</TermLabel></div>
                {forms ? (
                  <div className="text-sm space-y-1">
                    <div>{forms.version ? `Form records v${forms.version}: ${forms.records.length} × 1095-C` : "No form records generated."}</div>
                    {forms.generatedPdfs.length === 0 ? <div className="text-muted-foreground">No PDFs saved.</div> : (
                      <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                        {forms.generatedPdfs.map((p) => (
                          <li key={p.id} className="text-xs flex items-center gap-2">
                            <Badge variant="outline">{p.formType === "1095c" ? "1095-C" : "1094-C"} v{p.version}</Badge>
                            <span>{p.employeeId ? `employee #${p.employeeId}` : "employer"}</span>
                            <span className="text-muted-foreground">{fmt(p.generatedAt)}</span>
                            {p.pdfUrl && <a className="underline" href={p.pdfUrl} target="_blank" rel="noreferrer">open</a>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : <div className="text-sm text-muted-foreground">Loading…</div>}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Workforce snapshots: frozen roster per calendar year (data history + ALE basis)
// ---------------------------------------------------------------------------
interface WorkforceSnapshotRow {
  id: number; snapshotYear: number; asOf: string; kind: "year_end" | "manual"; status: "frozen" | "superseded";
  employeeCount: number; summary: { monthlyFullTime?: number[]; monthlyFte?: number[]; monthlyTotal?: number[]; estimated?: boolean; monthsWithData?: number };
  notes: string | null; takenAt: string | null; takenByUserId: string | null; supersededAt: string | null; supersedeReason: string | null;
}
interface WorkforceSnapshotEmployee {
  employeeId: number; firstName: string | null; lastName: string | null; employmentType: string | null; status: string | null;
  hireDate: string | null; terminationDate: string | null; scheduledHoursPerWeek: string | null; compensationType: string | null; compensationAmount: string | null;
  monthlyHours: Record<string, number | null>; monthlyStatus: Record<string, string>; monthsEmployed: number; monthsFullTime: number;
}

function WorkforceSnapshotsCard({ clientId }: { clientId?: number }) {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);
  const [kind, setKind] = useState<"manual" | "year_end">("manual");
  const [reason, setReason] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);

  const { data: snapshots = [], isLoading } = useQuery<WorkforceSnapshotRow[]>({
    queryKey: ["/api/aca/workforce-snapshots", clientId],
    queryFn: async () => { const r = await fetch(`/api/aca/workforce-snapshots?clientId=${clientId}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load workforce snapshots"); return r.json(); },
    enabled: !!clientId,
  });
  const { data: detail } = useQuery<{ snapshot: WorkforceSnapshotRow; employees: WorkforceSnapshotEmployee[] }>({
    queryKey: ["/api/aca/workforce-snapshots", clientId, openId],
    queryFn: async () => { const r = await fetch(`/api/aca/workforce-snapshots/${openId}?clientId=${clientId}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load snapshot"); return r.json(); },
    enabled: !!clientId && openId != null,
  });
  const take = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/aca/workforce-snapshots", { clientId, year, kind, reason: reason || undefined }),
    onSuccess: () => {
      toast({ title: "Workforce snapshot taken", description: `${kind === "year_end" ? "Year-end" : "Manual"} snapshot of ${year} frozen.` });
      setReason("");
      for (const key of ["/api/aca/workforce-snapshots", "/api/aca/dashboard", "/api/aca/year", "/api/aca/audit-log"]) queryClient.invalidateQueries({ queryKey: [key] });
    },
    onError: async (e: any) => toast({ title: "Snapshot failed", description: e?.message || String(e), variant: "destructive" }),
  });

  const avg = (s: WorkforceSnapshotRow["summary"]) => {
    if (!s?.monthlyFullTime) return null;
    const sum = s.monthlyFullTime.reduce((a, b, i) => a + b + (s.monthlyFte?.[i] ?? 0), 0);
    return Math.floor(sum / 12);
  };
  const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : "—");
  const years = Array.from({ length: currentYear - 2014 }, (_, i) => currentYear - i);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Camera className="h-4 w-4" /> <TermLabel term="yearEndSnapshot">Workforce Snapshots</TermLabel></CardTitle>
        <CardDescription>
          A frozen copy of the roster for a calendar year: employment periods, scheduled hours, compensation, monthly hours of service and the
          monthly full-time determination for every employee. The year-end snapshot is taken automatically in early January and is the basis
          of the following year's ALE status; later HR edits never change it. Take a manual snapshot any time for your records.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Calendar year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs"><TermLabel term={kind === "year_end" ? "yearEndSnapshot" : "manualSnapshot"}>Kind</TermLabel></Label>
            <Select value={kind} onValueChange={(v) => setKind(v as any)}>
              <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (record only)</SelectItem>
                <SelectItem value="year_end">Year-end (replaces ALE basis)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kind === "year_end" && (
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs"><TermLabel term="override">Reason (required, audited)</TermLabel></Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the frozen basis must be replaced" />
            </div>
          )}
          <Button onClick={() => take.mutate()} disabled={take.isPending || !clientId || (kind === "year_end" && !reason.trim())}>
            {take.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}Take snapshot
          </Button>
        </div>

        {isLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : snapshots.length === 0 ? (
          <div className="text-sm text-muted-foreground">No snapshots yet. The first year-end snapshot is taken automatically in January.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30px]" />
                <TableHead>Year</TableHead>
                <TableHead><TermLabel term="yearEndSnapshot">Kind</TermLabel></TableHead>
                <TableHead><TermLabel term="snapshotStatus">Status</TermLabel></TableHead>
                <TableHead><TermLabel term="population">Employees</TermLabel></TableHead>
                <TableHead><TermLabel term="aleAverage">Avg FT + FTE</TermLabel></TableHead>
                <TableHead>Taken</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {snapshots.map((s) => (
                <>
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => setOpenId(openId === s.id ? null : s.id)}>
                    <TableCell>{openId === s.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                    <TableCell className="font-medium">{s.snapshotYear}</TableCell>
                    <TableCell>{s.kind === "year_end" ? <Explained term="yearEndSnapshot"><Badge className="bg-blue-600">Year-end (ALE basis)</Badge></Explained> : <Explained term="manualSnapshot"><Badge variant="secondary">Manual</Badge></Explained>}</TableCell>
                    <TableCell><Explained term="snapshotStatus">{s.status === "frozen" ? <Badge className="bg-green-600">Frozen</Badge> : <Badge variant="outline">Superseded</Badge>}</Explained></TableCell>
                    <TableCell>{s.employeeCount}</TableCell>
                    <TableCell>{avg(s.summary) ?? "—"}{s.summary?.estimated ? <Explained term="aleEstimated"><span className="text-xs text-muted-foreground"> (est.)</span></Explained> : null}</TableCell>
                    <TableCell className="text-xs">{fmt(s.takenAt)}{s.takenByUserId ? "" : " · automatic"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.notes || ""}{s.status === "superseded" && s.supersedeReason ? ` · superseded: ${s.supersedeReason}` : ""}</TableCell>
                  </TableRow>
                  {openId === s.id && (
                    <TableRow key={`${s.id}-detail`}>
                      <TableCell colSpan={8} className="bg-muted/30">
                        {!detail ? <div className="text-xs text-muted-foreground py-2">Loading employees…</div> : (
                          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Employee</TableHead>
                                  <TableHead>Hired</TableHead>
                                  <TableHead>Terminated</TableHead>
                                  <TableHead><TermLabel term="hoursEstimated">Hrs/week</TermLabel></TableHead>
                                  <TableHead><TermLabel term="compensationHistory">Compensation</TermLabel></TableHead>
                                  <TableHead><TermLabel term="population">Months employed</TermLabel></TableHead>
                                  <TableHead><TermLabel term="fullTime">Full-time months</TermLabel></TableHead>
                                  <TableHead><TermLabel term="monthlyStatusLegend">Monthly status</TermLabel></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {detail.employees.map((e) => (
                                  <TableRow key={e.employeeId}>
                                    <TableCell className="text-xs font-medium">{e.firstName} {e.lastName}</TableCell>
                                    <TableCell className="text-xs">{e.hireDate ? formatDateDisplay(e.hireDate) : "—"}</TableCell>
                                    <TableCell className="text-xs">{e.terminationDate ? formatDateDisplay(e.terminationDate) : "—"}</TableCell>
                                    <TableCell className="text-xs">{e.scheduledHoursPerWeek ?? "—"}</TableCell>
                                    <TableCell className="text-xs">{e.compensationAmount ? `$${Number(e.compensationAmount).toLocaleString()} ${e.compensationType === "hourly" ? "/hr" : "/yr"}` : "—"}</TableCell>
                                    <TableCell className="text-xs">{e.monthsEmployed}</TableCell>
                                    <TableCell className="text-xs">{e.monthsFullTime}</TableCell>
                                    <TableCell>
                                      <div className="flex gap-0.5">
                                        {Array.from({ length: 12 }, (_, i) => {
                                          const st = e.monthlyStatus[String(i + 1)];
                                          const cls = st === "full_time" ? "bg-green-500" : st === "non_full_time" ? "bg-gray-400" : st === "unknown" ? "bg-red-400" : "bg-transparent border";
                                          const h = e.monthlyHours[String(i + 1)];
                                          return <span key={i} title={`Month ${i + 1}: ${st ?? "not employed"}${h != null ? ` · ${Number(h).toFixed(1)} h` : ""}`} className={`inline-block w-2.5 h-2.5 rounded-sm ${cls}`} />;
                                        })}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
